// src/pages/PftFilterPanel.js
//
// "Filter by PFT values" on the PFT Uploads page: rules like QueryRegX's over the
// extracted values (rules and matching in services/pftCardFilter.js). Starts closed with
// no rules, so the page looks and behaves exactly as before until someone adds one.

import { useState } from "react";
import { Filter, Plus, X, ChevronDown } from "lucide-react";
import { PFT_FILTER_FIELD_GROUPS, PFT_FILTER_OPERATORS, isActiveRule } from "../services/pftCardFilter";

const inputCls =
  "w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition";
const btn =
  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[12px] text-slate-200 transition disabled:opacity-40";
const optionCls = "bg-[#0b0820] text-slate-100";

export const emptyRule = () => ({ field: "", operator: ">", value: "" });

export default function PftFilterPanel({ rules, setRules, mode, setMode, matchCount, totalCount }) {
  const [open, setOpen] = useState(false);
  const active = rules.filter(isActiveRule).length;
  const update = (i, key, value) => setRules(rules.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
  const add = () => setRules([...rules, emptyRule()]);
  const remove = (i) => setRules(rules.filter((_, j) => j !== i));
  const toggle = () => {
    if (!open && !rules.length) setRules([emptyRule()]);
    setOpen(!open);
  };

  return (
    <div className="mb-3 rounded-2xl border border-white/10 bg-white/[0.02]">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-[13px] text-slate-200"
      >
        <Filter className="w-4 h-4" />
        Filter by PFT values
        {active > 0 && (
          <span className="text-[11px] px-2 py-0.5 rounded-full border bg-purple-500/15 border-purple-300/30 text-purple-200">
            {active} active · {matchCount} of {totalCount} match
          </span>
        )}
        <ChevronDown className={`w-4 h-4 ml-auto transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2">
          <p className="text-[11px] text-slate-400">
            Checks each patient's <span className="font-semibold">newest</span> PFT. A patient without that value doesn't
            match. Ratios can be typed as 0.70 or 70.
          </p>

          {rules.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(0,2fr)_6rem_minmax(0,1fr)_auto] gap-2 items-center">
              <select
                value={r.field}
                onChange={(e) => update(i, "field", e.target.value)}
                aria-label={`Filter ${i + 1} value`}
                className={`${inputCls} col-span-2 sm:col-span-1`}
              >
                <option value="" className={optionCls}>Choose a PFT value…</option>
                {PFT_FILTER_FIELD_GROUPS.map((g) => (
                  <optgroup key={g.title} label={g.title} className={optionCls}>
                    {g.fields.map((f) => (
                      <option key={f.key} value={f.key} className={optionCls}>
                        {f.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <select
                value={r.operator}
                onChange={(e) => update(i, "operator", e.target.value)}
                aria-label={`Filter ${i + 1} comparison`}
                className={inputCls}
              >
                {PFT_FILTER_OPERATORS.map((o) => (
                  <option key={o} value={o} className={optionCls}>
                    {o}
                  </option>
                ))}
              </select>
              <input
                value={r.value}
                onChange={(e) => update(i, "value", e.target.value)}
                inputMode="decimal"
                placeholder="Number"
                aria-label={`Filter ${i + 1} number`}
                className={inputCls}
              />
              <button type="button" onClick={() => remove(i)} aria-label={`Remove filter ${i + 1}`} className={btn}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button type="button" onClick={add} className={btn}>
              <Plus className="w-3.5 h-3.5" />
              Add filter
            </button>
            {rules.length > 1 && (
              <div className="inline-flex items-center gap-1 rounded-lg border border-white/10 p-0.5" role="group" aria-label="Combine filters">
                {[
                  { key: "all", label: "Match all filters" },
                  { key: "any", label: "Match any filter" },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    aria-pressed={mode === m.key}
                    onClick={() => setMode(m.key)}
                    className={`px-2.5 py-1 rounded-md text-[12px] transition ${
                      mode === m.key ? "bg-purple-500/20 text-white" : "text-slate-300 hover:bg-white/[0.08]"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}
            {rules.length > 0 && (
              <button type="button" onClick={() => setRules([])} className={`${btn} ml-auto`}>
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
